import { ApolloError, MutationResult, MutationTuple, useMutation } from "@apollo/client"
import { MutationHookOptions } from "@apollo/client/react/types/types"
import { DocumentNode } from "graphql"
import { useState } from "react"

// https://github.com/apollographql/apollo-feature-requests/issues/170

interface IResettableMutationState<TData = any>
    extends Pick<MutationResult<TData>, "data" | "called" | "loading" | "error"> {}

export function useResettableMutation<TData = any, TVariables = any>(
    query: DocumentNode,
    props: MutationHookOptions<TData, TVariables>
): [MutationTuple<TData, TVariables>[0], MutationResult<TData> & { reset: () => void }] {
    const [{ loading, data, error, called }, setState] = useState<IResettableMutationState>({
            called: false,
            loading: false
        }),
        onCompleted = (d: TData) => {
            setState({ data: d, loading: false, called, error })
            props.onCompleted && props.onCompleted(data)
        },
        onError = (e: ApolloError) => {
            setState({ loading: false, called, error: e })
            props.onError && props.onError(e)
        },
        reset = () => {
            setState({ data: undefined, loading: false, called: false, error: undefined })
        }

    const [mutate, { client }] = useMutation<TData, TVariables>(query, {
            ...props,
            onCompleted,
            onError
        }),
        mutateWrapper = (opts: Parameters<MutationTuple<TData, TVariables>[0]>[0]) => {
            setState({ data: undefined, loading: true, called: true, error: undefined })
            return mutate(opts)
        }

    return [mutateWrapper, { data, loading, error, called, client, reset }]
}
